import { Component, inject, DestroyRef, input, computed, effect, output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormRecord, Validators } from '@angular/forms';
import {
  DsmAddendumOptionsPayload,
  DsmDealOption,
  DsmDealOptionProduct
} from '../../../models/dsm-deal-options-view.models';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { DropdownData } from '@zayan-erp/shared';
import {
  DsmProductAttributeGroup,
  DsmProductAttributeValueSummary,
  ProjectModelRoomQuantityLookup
} from '../../../models/dsm-deal-options.models';
import { AbilityService, GenericDropdownComponent, CurrencyInputFieldComponent } from '@zayan-erp/shared';
import { ButtonModule } from '@openng/optimus-ui/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { EMPTY, catchError, filter, of, switchMap } from 'rxjs';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { LocationItem } from '@zayan-erp/home-building';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { Message } from '@openng/optimus-ui/message';

@Component({
  standalone: true,
  selector: 'dsm-aps-addendum-option-basic-details-form',
  imports: [
    GenericDropdownComponent,
    CurrencyInputFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    TextareaModule,
    ButtonModule,
    FloatLabelModule,
    InputNumberModule,
    ConfirmDialogModule,
    Message
  ],
  providers: [ConfirmationService],
  templateUrl: './dsm-aps-addendum-option-basic-details-form.component.html'
})
export class DsmApsAddendumOptionBasicDetailsFormComponent {
  readonly fb = inject(FormBuilder);
  readonly destroyRef = inject(DestroyRef);
  readonly modelElevationProductsService = inject(DsmModelElevationProductsService);
  readonly dealOptionsService = inject(DsmDealOptionsService);
  readonly confirmationService = inject(ConfirmationService);
  readonly abilityService = inject(AbilityService);
  readonly dealAddendum = input.required<DsmDealOption>();
  readonly addendumOption = input.required<DsmDealOptionProduct>();
  readonly isCancelled = input.required<boolean>();
  readonly isLocked = computed(() => this.dealAddendum().isLocked || this.isCancelled());
  /**
   * On a locked addendum an Admin or SuperAdmin may still move the option. Nothing else on it changes.
   * `isCancelled` is the addendum's flag; a line cancelled on its own reports it on the option itself.
   */
  readonly canMoveLockedOption = computed(
    () =>
      this.dealAddendum().isLocked &&
      !this.isCancelled() &&
      !this.addendumOption().isCancelled &&
      this.abilityService.ability.can('dsm-edit-locked-addendum-location', 'aps')
  );
  readonly isKitLinkedProduct = input<boolean>(false);

  onChanges = output<string>();

  productId = computed(() => this.addendumOption().productId!);

  readonly productLocationsResource = rxResourceFixed({
    params: () => ({ productId: this.productId()! }),
    defaultValue: [] as LocationItem[],
    stream: ({ params }) => {
      if (!params.productId) return of<LocationItem[]>([]);
      return this.modelElevationProductsService.getProductLocations(this.dealAddendum().id, params.productId);
    }
  });

  readonly productAttributesResource = rxResourceFixed({
    params: () => ({ productId: this.productId()! }),
    defaultValue: [] as DsmProductAttributeGroup[],
    stream: ({ params }) => {
      if (!params.productId) return of<DsmProductAttributeGroup[]>([]);
      return this.modelElevationProductsService.getProductAttributes(params.productId);
    }
  });

  readonly locationOptions = computed<DropdownData[]>(() =>
    (this.productLocationsResource.value() ?? []).map(location => ({
      id: location.id,
      firstLabel: location.locationCode,
      secondLabel: location.locationName
    }))
  );

  productAttributes = computed(() => this.productAttributesResource.value() ?? []);

  readonly uofmLabel = computed(() => this.addendumOption().uofm?.code || this.addendumOption().uofm?.name || '');

  readonly noMeasurementDefined = signal(false);

  readonly attributeValuesMap = computed(() => {
    const map = new Map<string, DsmProductAttributeValueSummary[]>();
    this.productAttributes().forEach(attribute => {
      map.set(attribute.subCategoryAttributeId, attribute.productAttributeValues);
    });
    return map;
  });

  attributeValues = new FormRecord<FormControl<string | null>>({});

  productForm = this.fb.group({
    locationId: [null as string | null],
    quantity: [null as number | null, [Validators.required, Validators.min(1)]],
    unitPrice: [null as number | null, [Validators.required]],
    notes: [''],
    attributeValues: this.attributeValues
  });

  get attributeValuesControl(): FormRecord<FormControl<string | null>> {
    return this.attributeValues;
  }

  readonly savedVisible = signal(0);

  constructor() {
    this.productForm.controls.locationId.valueChanges
      .pipe(
        filter(() => !this.canMoveLockedOption()),
        switchMap(locationId => this.#lookupRoomQuantity(locationId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(lookup => this.#handleRoomQuantityLookup(lookup));

    // A locked option's new location is saved on its own: no room-quantity lookup, no re-price.
    this.productForm.controls.locationId.valueChanges
      .pipe(
        filter(locationId => this.canMoveLockedOption() && locationId !== this.addendumOption().selectedLocationId),
        switchMap(locationId =>
          this.dealOptionsService.updateAddendumOptionLocation(this.addendumOption().id, locationId).pipe(
            catchError(() => {
              this.productForm.controls.locationId.setValue(this.addendumOption().selectedLocationId, {
                emitEvent: false
              });
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.productForm.markAsPristine();
        this.savedVisible.update(n => n + 1);
        this.onChanges.emit(this.addendumOption().id);
      });
  }

  // Fetches the room quantity for a user-selected location, or resolves to null when the
  // lookup should be skipped (no location, locked form, missing uofm, or a failed request).
  #lookupRoomQuantity(locationId: string | null) {
    this.noMeasurementDefined.set(false);

    if (this.isLocked() || !locationId) {
      return of(null as ProjectModelRoomQuantityLookup | null);
    }

    const addendumId = this.dealAddendum().id;
    const productId = this.productId();
    const uofmId = this.addendumOption().uofm?.id;
    if (!addendumId || !productId || !uofmId) {
      return of(null as ProjectModelRoomQuantityLookup | null);
    }

    return this.modelElevationProductsService
      .getRoomQuantityLookup(addendumId, productId, locationId, uofmId)
      .pipe(catchError(() => of(null as ProjectModelRoomQuantityLookup | null)));
  }

  // Applies the room-quantity lookup result for a location change, then persists the
  // location (and, once confirmed, the new quantity) in a single auto-save.
  #handleRoomQuantityLookup(lookup: ProjectModelRoomQuantityLookup | null): void {
    if (!lookup) {
      this.onSubmit();
      return;
    }

    this.noMeasurementDefined.set(lookup.matchedShape === 'None');

    const currentQuantity = this.productForm.controls.quantity.value;
    if (lookup.matchedShape === 'None' || lookup.quantity === null || lookup.quantity === currentQuantity) {
      this.onSubmit();
      return;
    }

    const unitSuffix = this.uofmLabel() ? ` ${this.uofmLabel()}` : '';
    this.confirmationService.confirm({
      header: 'Quantity Updated',
      message: `Quantity has been updated to ${lookup.quantity}${unitSuffix} based on the selected location.`,
      acceptLabel: 'OK',
      rejectVisible: false,
      closable: false,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => {
        this.productForm.controls.quantity.setValue(lookup.quantity, { emitEvent: false });
        this.onSubmit();
      }
    });
  }

  onDealAddendumOptionDetailsChanges = effect(() => {
    this.productForm.patchValue(
      {
        locationId: this.addendumOption().selectedLocationId,
        quantity: this.addendumOption().quantity,
        unitPrice: this.addendumOption().unitPrice,
        notes: this.addendumOption().notes
      },
      { emitEvent: false }
    );
    this.productForm.markAsPristine();
    this.noMeasurementDefined.set(false);

    if (this.isLocked()) {
      this.productForm.disable({ emitEvent: false });
      if (this.canMoveLockedOption()) {
        this.productForm.controls.locationId.enable({ emitEvent: false });
      }
    } else {
      this.productForm.enable({ emitEvent: false });
      if (this.isKitLinkedProduct()) {
        this.productForm.controls.unitPrice.disable({ emitEvent: false });
      }
    }
  });

  // Effect to dynamically add/remove form controls based on attributes
  private setupAttributeControls = effect(() => {
    const attributes = this.productAttributes();
    const attributeValuesGroup = this.attributeValues;
    const existingAttributes = this.addendumOption().attributes || [];
    if (!attributes?.length) {
      return;
    }

    // Get current control keys
    const currentKeys = new Set(Object.keys(attributeValuesGroup.controls));
    const newKeys = new Set(attributes.map(attr => attr.subCategoryAttributeId));

    // Remove controls that are no longer needed
    currentKeys.forEach(key => {
      if (!newKeys.has(key)) {
        attributeValuesGroup.removeControl(key);
      }
    });

    // Add new controls and patch existing values
    attributes.forEach(attribute => {
      const attributeId = attribute.subCategoryAttributeId;

      // Find existing value for this attribute from addendumOption
      const existingAttribute = existingAttributes.find(attr => attr.subCategoryAttributeId === attributeId);

      // Get the first selected value ID if it exists
      const existingValueId = existingAttribute?.subCategoryAttributeValues?.[0]?.subCategoryAttributeValueId ?? null;

      if (!attributeValuesGroup.controls[attributeId]) {
        // Add new control with existing value if available
        attributeValuesGroup.addControl(attributeId, this.fb.control<string | null>(existingValueId), {
          emitEvent: false
        });
      } else {
        // Update existing control with value (for cases where form already exists)
        attributeValuesGroup.controls[attributeId].setValue(existingValueId, { emitEvent: false });
      }
    });
  });

  getAttributeSelectOptions(attributeId: string): (DsmProductAttributeValueSummary & { disabled: boolean })[] {
    return (this.attributeValuesMap().get(attributeId) ?? []).map(value => ({
      ...value,
      disabled: !value.isActive
    }));
  }

  // Get form control for a specific attribute
  getAttributeControl(attributeId: string): FormControl<string | null> | null {
    return this.attributeValuesControl.controls[attributeId] ?? null;
  }

  // Get form data in API payload format
  getFormData(): DsmAddendumOptionsPayload {
    const formValue = this.productForm.getRawValue();

    // Convert attributeValues object to array format for API
    const attributes = Object.entries(formValue.attributeValues || {})
      .filter(([_, valueId]) => valueId !== null)
      .map(([attributeId, valueId]) => ({
        subCategoryAttributeId: attributeId,
        subCategoryAttributeValueId: valueId as string
      }));

    const payload: DsmAddendumOptionsPayload = {
      modelElevationProductsId: this.addendumOption().modelElevationProductsId,
      selectedLocationId: formValue.locationId as string,
      quantity: formValue.quantity ?? this.addendumOption().quantity,
      unitPrice: formValue.unitPrice ?? 0,
      notes: formValue.notes || '',
      attributes
    };
    return payload;
  }

  // Check if form is valid
  isFormValid(): boolean {
    return this.productForm.valid;
  }

  onSubmit(): void {
    if (this.isLocked()) {
      return;
    }
    if (this.productForm.pristine) {
      return;
    }

    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }
    const payload = this.getFormData();

    this.dealOptionsService
      .updateAddendumOption(this.addendumOption().id, payload, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.productForm.markAsPristine();
          this.savedVisible.update(n => n + 1);
          this.onChanges.emit(this.addendumOption().id);
        }
      });
  }
}
